import React, { useState, useCallback, useMemo, Suspense } from "react";

// Lazy load form components for code splitting
const WFProcessForm = React.lazy(() => import("./WFProcessForm"));
const WFActionForm = React.lazy(() => import("./WFActionForm"));
const WFStepForm = React.lazy(() => import("./WFStepForm"));
const WFStatusForm = React.lazy(() => import("./WFStatusForm"));
const WFAssignmentForm = React.lazy(() => import("./WFAssignmentForm"));
const WFTransitionForm = React.lazy(() => import("./WFTransitionForm"));

const WorkflowSetupForms = React.memo(() => {
  const [activeTab, setActiveTab] = useState("Process");

  // Memoize the tab change handler to prevent unnecessary re-renders
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  // Memoize the tab content to avoid re-computing
  const tabContent = useMemo(() => {
    switch (activeTab) {
      case "Process":
        return <WFProcessForm />;
      case "Action":
        return <WFActionForm />;
      case "Step":
        return <WFStepForm />;
      case "Status":
        return <WFStatusForm />;
      case "Assignment":
        return <WFAssignmentForm />;
      case "Transition":
        return <WFTransitionForm />;
      default:
        return null;
    }
  }, [activeTab]);

  return (
    <div className="">
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Workflow Setup</h2>
      </div>
      {/* Bootstrap Nav Tabs */}
      <ul
        className="nav nav-tabs workflow-setup-tabs mb-3 gap-1"
        id="workflowTabs"
        role="tablist"
      >
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === "Process" ? "active" : ""}`}
            id="process-tab"
            data-bs-toggle="tab"
            data-bs-target="#process"
            type="button"
            role="tab"
            aria-controls="process"
            aria-selected={activeTab === "Process"}
            onClick={() => handleTabChange("Process")}
          >
            Process
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === "Action" ? "active" : ""}`}
            id="action-tab"
            data-bs-toggle="tab"
            data-bs-target="#action"
            type="button"
            role="tab"
            aria-controls="action"
            aria-selected={activeTab === "Action"}
            onClick={() => handleTabChange("Action")}
          >
            Action
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === "Step" ? "active" : ""}`}
            id="step-tab"
            data-bs-toggle="tab"
            data-bs-target="#step"
            type="button"
            role="tab"
            aria-controls="step"
            aria-selected={activeTab === "Step"}
            onClick={() => handleTabChange("Step")}
          >
            Step
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === "Status" ? "active" : ""}`}
            id="status-tab"
            data-bs-toggle="tab"
            data-bs-target="#status"
            type="button"
            role="tab"
            aria-controls="status"
            aria-selected={activeTab === "Status"}
            onClick={() => handleTabChange("Status")}
          >
            Status
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === "Assignment" ? "active" : ""}`}
            id="assignment-tab"
            data-bs-toggle="tab"
            data-bs-target="#assignment"
            type="button"
            role="tab"
            aria-controls="assignment"
            aria-selected={activeTab === "Assignment"}
            onClick={() => handleTabChange("Assignment")}
          >
            Assignment
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === "Transition" ? "active" : ""}`}
            id="transition-tab"
            data-bs-toggle="tab"
            data-bs-target="#transition"
            type="button"
            role="tab"
            aria-controls="transition"
            aria-selected={activeTab === "Transition"}
            onClick={() => handleTabChange("Transition")}
          >
            Transition
          </button>
        </li>
      </ul>

      {/* Tab Content with Suspense */}
      <div className="tab-content" id="workflowTabContent">
        <Suspense fallback={<div className="p-3">Loading...</div>}>
          <div
            className={`tab-pane fade ${
              activeTab === "Process" ? "show active" : ""
            }`}
            id="process"
            role="tabpanel"
            aria-labelledby="process-tab"
          >
            {activeTab === "Process" && tabContent}
          </div>
          <div
            className={`tab-pane fade ${
              activeTab === "Action" ? "show active" : ""
            }`}
            id="action"
            role="tabpanel"
            aria-labelledby="action-tab"
          >
            {activeTab === "Action" && tabContent}
          </div>
          <div
            className={`tab-pane fade ${
              activeTab === "Step" ? "show active" : ""
            }`}
            id="step"
            role="tabpanel"
            aria-labelledby="step-tab"
          >
            {activeTab === "Step" && tabContent}
          </div>
          <div
            className={`tab-pane fade ${
              activeTab === "Status" ? "show active" : ""
            }`}
            id="status"
            role="tabpanel"
            aria-labelledby="status-tab"
          >
            {activeTab === "Status" && tabContent}
          </div>
          <div
            className={`tab-pane fade ${
              activeTab === "Assignment" ? "show active" : ""
            }`}
            id="assignment"
            role="tabpanel"
            aria-labelledby="assignment-tab"
          >
            {activeTab === "Assignment" && tabContent}
          </div>
          <div
            className={`tab-pane fade ${
              activeTab === "Transition" ? "show active" : ""
            }`}
            id="transition"
            role="tabpanel"
            aria-labelledby="transition-tab"
          >
            {activeTab === "Transition" && tabContent}
          </div>
        </Suspense>
      </div>
    </div>
  );
});

export default WorkflowSetupForms;
